/**
 * useAttachments Hook
 * 
 * Stateless utility hook for uploading and downloading file attachments
 * via backend API endpoints. Backend handles all storage configuration.
 * 
 * ARCHITECTURE:
 * - Stateless - no client-facing state management
 * - No storage config on frontend - all via API
 * - Separation of concerns with attachFromDevice handler
 * - Extensible for future upload strategies (multipart, chunking)
 * 
 * FLOW:
 * 1. Request upload URL from API → Backend generates signed URL
 * 2. Upload file directly to storage (MinIO/S3/R2)
 * 3. Optionally request preview URL from API
 * 4. Notify API of upload completion
 * 
 * USAGE:
 * ```tsx
 * const { uploadFile, getDownloadUrl } = useAttachments({
 *   apiBaseUrl: '/api', // Edge service API
 * });
 * 
 * // Use with attachFromDevice handler
 * const handler = createAttachFromDeviceHandler({
 *   onFilesAdded: async (files) => {
 *     for (const file of files) {
 *       const result = await uploadFile(file);
 *       // Handle result (update UI, send message, etc.)
 *     }
 *   }
 * });
 * ```
 */

import { useCallback } from 'react';
import type { 
  UploadResult, 
  UploadOptions, 
  DownloadOptions,
  UploadProgress 
} from '../types/attachment';

export interface UseAttachmentsConfig {
  /**
   * Base URL for storage API endpoints
   * @example '/api' → calls /api/storage/upload-url
   */
  apiBaseUrl: string;
  
  /**
   * Default bucket for uploads
   * @default 'attachments'
   */
  defaultBucket?: string;
  
  /**
   * Enable debug logging
   * @default false
   */
  enableDebugLogging?: boolean;
  
  /**
   * Fetch options (headers, credentials, etc.)
   */
  fetchOptions?: RequestInit;
}

export interface UseAttachmentsReturn {
  /**
   * Upload a file to storage via API
   * Returns upload result with storage key and optional preview URL
   */
  uploadFile: (file: File, options?: UploadOptions) => Promise<UploadResult>;
  
  /**
   * Get a signed download/preview URL for an existing file
   */
  getDownloadUrl: (key: string, options?: DownloadOptions) => Promise<string>;
  
  /**
   * Get a signed HEAD URL to check if file exists
   */
  checkFileExists: (key: string) => Promise<boolean>;
}

// API request/response types matching middleware
interface UploadUrlRequest {
  filename: string;
  contentType: string;
  bucket?: string;
  metadata?: Record<string, string>;
}

interface UploadUrlResponse {
  uploadUrl: string;
  key: string;
  expiresAt: number;
}

interface DownloadUrlRequest {
  key: string;
  bucket?: string;
  httpMethod?: 'GET' | 'HEAD';
  ttl?: number;
}

interface DownloadUrlResponse {
  downloadUrl: string;
  expiresAt: number;
}

interface UploadCompleteRequest {
  key: string;
  bucket?: string;
  size: number;
  metadata?: Record<string, string>;
}

/**
 * Hook for managing file attachments via backend API
 */
export function useAttachments(config: UseAttachmentsConfig): UseAttachmentsReturn {
  const {
    apiBaseUrl,
    defaultBucket = 'attachments',
    enableDebugLogging = false,
    fetchOptions = {},
  } = config;

  /**
   * Get a signed download URL for an existing file via API
   */
  const getDownloadUrl = useCallback(async (
    key: string,
    options: DownloadOptions = {}
  ): Promise<string> => {
    const {
      httpMethod = 'GET',
      ttl = 3600,
    } = options;

    const bucket = defaultBucket;

    try {
      if (enableDebugLogging) {
        console.log('[useAttachments] Requesting download URL:', { key, httpMethod, ttl });
      }

      const downloadUrlRequest: DownloadUrlRequest = {
        key,
        bucket,
        httpMethod,
        ttl,
      };

      const response = await fetch(`${apiBaseUrl}/storage/download-url`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...fetchOptions.headers,
        },
        credentials: 'include',
        ...fetchOptions,
        body: JSON.stringify(downloadUrlRequest),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || `API error: ${response.status}`);
      }

      const { downloadUrl }: DownloadUrlResponse = await response.json();

      return downloadUrl;
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : String(error);
      console.error('[useAttachments] Failed to generate download URL:', errorMsg);
      throw new Error(`Failed to generate download URL: ${errorMsg}`);
    }
  }, [apiBaseUrl, defaultBucket, enableDebugLogging, fetchOptions]);

  /**
   * Upload a file to storage via API
   */
  const uploadFile = useCallback(async (
    file: File,
    options: UploadOptions = {}
  ): Promise<UploadResult> => {
    const {
      method = 'simple',
      generatePreview = false,
      onProgress,
      metadata = {},
    } = options;

    const bucket = defaultBucket;

    try {
      if (enableDebugLogging) {
        console.log('[useAttachments] Starting upload:', {
          filename: file.name,
          size: file.size,
          type: file.type,
          method,
        });
      }

      // Step 1: Request upload URL from API
      const uploadUrlRequest: UploadUrlRequest = {
        filename: file.name,
        contentType: file.type || 'application/octet-stream',
        bucket,
        metadata,
      };

      const uploadUrlResponse = await fetch(`${apiBaseUrl}/storage/upload-url`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...fetchOptions.headers,
        },
        credentials: 'include', // Include cookies for auth
        ...fetchOptions,
        body: JSON.stringify(uploadUrlRequest),
      });

      if (!uploadUrlResponse.ok) {
        const error = await uploadUrlResponse.json();
        throw new Error(error.error || `API error: ${uploadUrlResponse.status}`);
      }

      const { uploadUrl, key, expiresAt }: UploadUrlResponse = await uploadUrlResponse.json();

      if (enableDebugLogging) {
        console.log('[useAttachments] Upload URL received:', {
          key,
          expiresAt: new Date(expiresAt).toISOString(),
        });
      }

      // Step 2: Upload file to storage based on method
      let uploadSuccess = false;

      switch (method) {
        case 'simple':
          uploadSuccess = await uploadSimple(file, uploadUrl, onProgress);
          break;
        
        case 'multipart':
          // Future: Implement multipart upload via API
          throw new Error('Multipart upload not yet implemented');
        
        case 'chunked':
          // Future: Implement custom chunking
          throw new Error('Chunked upload not yet implemented');
        
        default:
          throw new Error(`Unknown upload method: ${method}`);
      }

      if (!uploadSuccess) {
        throw new Error('Upload to storage failed');
      }

      if (enableDebugLogging) {
        console.log('[useAttachments] Upload completed:', key);
      }

      const uploadedAt = new Date();

      // Step 3: Notify API of upload completion
      const completeRequest: UploadCompleteRequest = {
        key,
        bucket,
        size: file.size,
        metadata: {
          ...metadata,
          originalFilename: file.name,
        },
      };

      await fetch(`${apiBaseUrl}/storage/upload-complete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...fetchOptions.headers,
        },
        credentials: 'include',
        ...fetchOptions,
        body: JSON.stringify(completeRequest),
      });

      // Step 4: Optionally generate preview URL
      let previewUrl: string | undefined;
      if (generatePreview) {
        previewUrl = await getDownloadUrl(key, { ttl: 3600 });

        if (enableDebugLogging) {
          console.log('[useAttachments] Preview URL generated');
        }
      }

      return {
        key,
        filename: file.name,
        size: file.size,
        contentType: file.type || 'application/octet-stream',
        uploadedAt,
        previewUrl,
      };

    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : String(error);
      console.error('[useAttachments] Upload failed:', errorMsg);
      throw new Error(`Failed to upload file: ${errorMsg}`);
    }
  }, [apiBaseUrl, defaultBucket, enableDebugLogging, fetchOptions, getDownloadUrl]);

  /**
   * Check if a file exists using HEAD request
   */
  const checkFileExists = useCallback(async (key: string): Promise<boolean> => {
    try {
      const headUrl = await getDownloadUrl(key, { httpMethod: 'HEAD', ttl: 60 });
      const response = await fetch(headUrl, { method: 'HEAD' });
      return response.ok;
    } catch (error) {
      if (enableDebugLogging) {
        console.warn('[useAttachments] File existence check failed:', error);
      }
      return false;
    }
  }, [getDownloadUrl, enableDebugLogging]);

  return {
    uploadFile,
    getDownloadUrl,
    checkFileExists,
  };
}

/**
 * Simple upload strategy - single PUT request
 * Browser automatically handles chunking for large files
 */
async function uploadSimple(
  file: File,
  signedUrl: string,
  onProgress?: (progress: UploadProgress) => void
): Promise<boolean> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    // Track upload progress
    if (onProgress) {
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          const progress: UploadProgress = {
            loaded: event.loaded,
            total: event.total,
            percentage: Math.round((event.loaded / event.total) * 100),
          };
          onProgress(progress);
        }
      });
    }

    // Handle completion
    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(true);
      } else {
        reject(new Error(`Upload failed with status ${xhr.status}`));
      }
    });

    // Handle errors
    xhr.addEventListener('error', () => {
      reject(new Error('Upload network error'));
    });

    xhr.addEventListener('abort', () => {
      reject(new Error('Upload aborted'));
    });

    // Execute upload
    xhr.open('PUT', signedUrl);
    xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
    xhr.send(file);
  });
}

/**
 * Future: Multipart upload strategy for large files
 * Placeholder for extensibility
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
async function uploadMultipart(
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _file: File,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _config: {
    bucket: string;
    key: string;
    partSize?: number;
  },
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _onProgress?: (progress: UploadProgress) => void
): Promise<boolean> {
  // TODO: Implement S3 multipart upload
  // 1. Initiate multipart upload
  // 2. Upload parts in chunks
  // 3. Complete multipart upload
  throw new Error('Multipart upload not yet implemented');
}

/**
 * Future: Custom chunked upload strategy
 * Placeholder for extensibility
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
async function uploadChunked(
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _file: File,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _signedUrl: string,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _options: {
    chunkSize?: number;
  },
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _onProgress?: (progress: UploadProgress) => void
): Promise<boolean> {
  // TODO: Implement custom chunking strategy
  throw new Error('Chunked upload not yet implemented');
}

